import {
  CalendarIcon,
  ClipboardDocumentListIcon,
  ClockIcon,
  UserMinusIcon,
} from '@heroicons/react/24/outline';

import { SummaryCounts } from '../api/types';

// The four top-level dashboard views. Calendar has no badge; the three queue
// tabs each show a live count badge from the summary endpoint. This phase
// only the Calendar body is built — the queue tabs render a placeholder.

export type DashboardTab =
  | 'calendar'
  | 'no_show'
  | 'missing_timesheet'
  | 'needs_approval';

type Props = {
  active: DashboardTab;
  onChange: (tab: DashboardTab) => void;
  counts?: SummaryCounts;
};

const TabBar = ({ active, onChange, counts }: Props) => {
  const tab = (
    key: DashboardTab,
    label: string,
    Icon: typeof CalendarIcon,
    badge?: number,
    // Below 1440 the design drops the queue labels to icon + badge and keeps
    // only Calendar's (Figma `Admin-Dashboard x 1024`: Calendar 125px, the rest
    // 84px = 16 + 20 + 12 + badge + 16). See `is-labelled` in the stylesheet.
    keepLabel = false,
  ) => (
    <button
      type="button"
      className={`dashv2-tab${active === key ? ' is-active' : ''}${
        keepLabel ? ' is-labelled' : ''
      }`}
      onClick={() => onChange(key)}
      id={`dashv2-tab-${key}`}
    >
      <Icon />
      {/* Always rendered. Narrow widths hide it VISUALLY only, so the button
          keeps its accessible name — `display: none` would leave a screen
          reader with an unnamed tab and a number. */}
      <span className="dashv2-tab-label">{label}</span>
      {badge != null && <span className="dashv2-tab-badge">{badge}</span>}
    </button>
  );

  return (
    <div className="dashv2-tabs" role="tablist">
      {tab('calendar', 'Calendar', CalendarIcon, undefined, true)}
      {tab('no_show', 'No show', UserMinusIcon, counts?.no_show)}
      {tab(
        'missing_timesheet',
        'Missing timesheet',
        ClipboardDocumentListIcon,
        counts?.missing_timesheet,
      )}
      {tab(
        'needs_approval',
        'Needs approval',
        ClockIcon,
        counts?.needs_approval,
      )}
    </div>
  );
};

export default TabBar;
